/**
 * Northstar-owned trip support — FBO / fuel / catering / hotel checklist
 * on the canonical mission ticket. Not a FlightBridge / FuelerLinx adapter.
 */

import { sanitizeClockIso } from "./clock";
import { DEFAULT_CURRENCY, hydrateCurrency, type CurrencyCode } from "./catalogs";
import { missionTicketRef } from "./flightNumber";
import { safeTrim } from "./text";
import type { Mission, TripSupportLine, TripSupportServiceType, TripSupportStatus } from "./types";

export const TRIP_SUPPORT_SERVICES: readonly TripSupportServiceType[] = [
  "handling",
  "fuel",
  "catering",
  "hotel",
  "ground",
  "customs",
  "other",
];

export const TRIP_SUPPORT_STATUSES: readonly TripSupportStatus[] = ["requested", "confirmed", "cancelled"];

export const TRIP_SUPPORT_SERVICE_LABEL: Record<TripSupportServiceType, string> = {
  handling: "Handling / FBO",
  fuel: "Fuel",
  catering: "Catering",
  hotel: "Hotel",
  ground: "Ground transport",
  customs: "Customs / permits",
  other: "Other",
};

export const TRIP_SUPPORT_STATUS_LABEL: Record<TripSupportStatus, string> = {
  requested: "Requested",
  confirmed: "Confirmed",
  cancelled: "Cancelled",
};

const SERVICE_SET: ReadonlySet<string> = new Set(TRIP_SUPPORT_SERVICES);
const STATUS_SET: ReadonlySet<string> = new Set(TRIP_SUPPORT_STATUSES);

export function isTripSupportServiceType(value: unknown): value is TripSupportServiceType {
  return typeof value === "string" && SERVICE_SET.has(value);
}

export function isTripSupportStatus(value: unknown): value is TripSupportStatus {
  return typeof value === "string" && STATUS_SET.has(value);
}

export function newTripSupportId(now = Date.now()): string {
  return `ts-${now.toString(36)}-${Math.floor(Math.random() * 1e4).toString(36)}`;
}

function optionalCost(value: unknown): number | undefined {
  if (value == null || value === "") return undefined;
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n) || n < 0) return undefined;
  return Math.round(n * 100) / 100;
}

function optionalIcao(value: unknown): string {
  return safeTrim(value).toUpperCase();
}

export function hydrateTripSupportLine(raw: unknown, index = 0): TripSupportLine | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const row = raw as Partial<TripSupportLine>;
  const id = safeTrim(row.id) || `ts-hydrate-${index}`;
  return {
    id,
    airportIcao: optionalIcao(row.airportIcao),
    serviceType: isTripSupportServiceType(row.serviceType) ? row.serviceType : "other",
    vendorName: safeTrim(row.vendorName),
    status: isTripSupportStatus(row.status) ? row.status : "requested",
    estimatedCost: optionalCost(row.estimatedCost),
    currency: row.currency != null ? hydrateCurrency(row.currency) : undefined,
    notes: safeTrim(row.notes),
    confirmByIso: sanitizeClockIso(row.confirmByIso) || undefined,
    legSeq: typeof row.legSeq === "number" && Number.isFinite(row.legSeq) ? row.legSeq : undefined,
  };
}

export function hydrateTripSupport(raw: unknown): TripSupportLine[] {
  if (!Array.isArray(raw)) return [];
  const out: TripSupportLine[] = [];
  raw.forEach((row, index) => {
    const line = hydrateTripSupportLine(row, index);
    if (line) out.push(line);
  });
  return out;
}

export function blankTripSupportLine(input: {
  airportIcao?: string;
  serviceType?: TripSupportServiceType;
  currency?: CurrencyCode;
  now?: number;
}): TripSupportLine {
  return {
    id: newTripSupportId(input.now),
    airportIcao: optionalIcao(input.airportIcao),
    serviceType: input.serviceType ?? "handling",
    vendorName: "",
    status: "requested",
    currency: hydrateCurrency(input.currency ?? DEFAULT_CURRENCY),
    notes: "",
  };
}

export function tripSupportOf(mission: Pick<Mission, "tripSupport"> | null | undefined): TripSupportLine[] {
  return Array.isArray(mission?.tripSupport) ? mission.tripSupport : [];
}

/** Open day-of items — Requested only (confirmed / cancelled do not chip). */
export function openTripSupportCount(mission: Pick<Mission, "tripSupport"> | null | undefined): number {
  return tripSupportOf(mission).filter((line) => line.status === "requested").length;
}

export function tripSupportChipLabel(count: number): string {
  if (count <= 0) return "";
  return count === 1 ? "Support 1" : `Support ${count}`;
}

export function tripSupportAirports(mission: Pick<Mission, "originIcao" | "destIcao" | "legs" | "overnightAtIcao">): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  const push = (icao: unknown) => {
    const next = optionalIcao(icao);
    if (!next || seen.has(next)) return;
    seen.add(next);
    out.push(next);
  };
  push(mission.originIcao);
  push(mission.destIcao);
  for (const leg of mission.legs ?? []) {
    push(leg.originIcao);
    push(leg.destIcao);
  }
  push(mission.overnightAtIcao);
  return out;
}

/** Board / Gantt / Support pop-out all key off this — never an Inbox RFQ id. */
export function tripSupportTicketRef(mission: Pick<Mission, "id" | "flightNumber" | "callsign">): {
  missionId: string;
  flightNumber: string;
} {
  return missionTicketRef(mission);
}

export function applyTripSupportOp(
  lines: TripSupportLine[],
  action: {
    op: "add" | "update" | "remove";
    lineId?: string;
    airportIcao?: string;
    serviceType?: TripSupportServiceType;
    vendorName?: string;
    status?: TripSupportStatus;
    estimatedCost?: number;
    currency?: CurrencyCode;
    notes?: string;
    confirmByIso?: string;
  },
): TripSupportLine[] {
  const current = hydrateTripSupport(lines);
  if (action.op === "remove") {
    const id = safeTrim(action.lineId);
    return id ? current.filter((line) => line.id !== id) : current;
  }
  if (action.op === "update") {
    const id = safeTrim(action.lineId);
    if (!id) return current;
    return current.map((line) => {
      if (line.id !== id) return line;
      return hydrateTripSupportLine({
        ...line,
        airportIcao: action.airportIcao ?? line.airportIcao,
        serviceType: action.serviceType ?? line.serviceType,
        vendorName: action.vendorName ?? line.vendorName,
        status: action.status ?? line.status,
        estimatedCost: action.estimatedCost ?? line.estimatedCost,
        currency: action.currency ?? line.currency,
        notes: action.notes ?? line.notes,
        confirmByIso: action.confirmByIso ?? line.confirmByIso,
      })!;
    });
  }
  const next = blankTripSupportLine({
    airportIcao: action.airportIcao,
    serviceType: action.serviceType,
    currency: action.currency,
  });
  return hydrateTripSupport([
    ...current,
    {
      ...next,
      vendorName: action.vendorName ?? next.vendorName,
      status: action.status ?? next.status,
      estimatedCost: action.estimatedCost,
      notes: action.notes ?? "",
      confirmByIso: action.confirmByIso,
    },
  ]);
}

export function tripSupportSearchHay(mission: Pick<Mission, "tripSupport">): string {
  return tripSupportOf(mission)
    .flatMap((line) => [
      line.airportIcao,
      line.vendorName,
      line.notes,
      TRIP_SUPPORT_SERVICE_LABEL[line.serviceType],
      line.serviceType,
      TRIP_SUPPORT_STATUS_LABEL[line.status],
    ])
    .join(" ");
}
